// Donut tile. V-shape-native: resolves the `:category` / `:value`
// (and optional scalar `:center`) `ColumnRef`s to columns, maps each
// row to a slice, and renders a recharts PieChart with an inner radius
// (always a donut). Slice fills route through the shared
// `categoryColorScale` so a category keeps a stable color across renders
// and draws from the same palette as the cartesian series. An optional
// center KPI readout overlays the donut hole.

import { useId } from 'react';
import { Label, Legend, Pie, PieChart, type PieLabelRenderProps, ResponsiveContainer, Sector, Tooltip } from 'recharts';

import {
  formatCompact,
  formatCurrency,
  formatInteger,
  formatNumber,
  formatPercent,
  numberHints,
} from '#core/components/dashboard/number-format.ts';
import type { DisplayFormat } from '#core/lib/expr/ops/dashboard/enums.ts';
import { type DonutSlice, donutSlices } from '#core/lib/expr/ops/dashboard/validate.ts';
import type { DonutSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { cellScalar } from '#core/lib/expr/value.ts';
import { categoryColorScale } from '#core/lib/formatters/categoryColors.ts';
import { mantineColorVar } from '#core/lib/formatters/colors.ts';
import { csvFromRows } from '#core/lib/transforms/csv-from-rows.ts';
import { slugify } from '#core/lib/utils/slugify.ts';

import { EMPTY } from './cell.ts';
import classes from './DonutPanel.module.css';
import { TileFrame } from './TileFrame.tsx';
import { useResolvedColumns } from './useResolvedColumns.ts';

/** Caption rendered for a tail-collapsed overflow slice. */
const OTHER_LABEL = 'Other';

/**
 * Per-format numeric renderers for the donut's `:format` token. The full
 * {@link DisplayFormat} union is accepted by the grammar, but a slice value
 * is always a number — non-numeric tokens (`text`, `datetime`, `sparkline`,
 * …) and the bare `number`/default fall through to a thousand-separated,
 * magnitude-inferred format keyed off the full slice set.
 */
const SLICE_FORMATTERS: Partial<Record<DisplayFormat, (n: number) => string>> = {
  compact: formatCompact,
  integer: formatInteger,
  percent: formatPercent,
  currency: formatCurrency,
};

/**
 * Collapse the tail past the top-N into a single `Other` slice. Slices are
 * taken in resolved (caller) order; when `maxSlices` is unset or the data
 * already fits, the input is returned untouched. The combined tail keeps the
 * sum of every collapsed slice's value.
 */
function capSlices(slices: readonly DonutSlice[], maxSlices: number | undefined): readonly DonutSlice[] {
  if (maxSlices === undefined || slices.length <= maxSlices) {
    return slices;
  }
  const head = slices.slice(0, maxSlices);
  const tail = slices.slice(maxSlices);
  const otherValue = tail.reduce((sum, s) => sum + s.value, 0);
  return [...head, { name: OTHER_LABEL, value: otherValue }];
}

/**
 * The number shown in the donut hole. An explicit numeric `:center` scalar
 * wins; when no `:center` ref was authored the slice total stands in (auto-sum);
 * a non-numeric explicit scalar yields `undefined` (rendered as the empty
 * placeholder).
 */
function pickCenterValue(
  centerScalar: number | string | null | undefined,
  centerOmitted: boolean,
  total: number,
): number | undefined {
  if (typeof centerScalar === 'number') {
    return centerScalar;
  }
  if (centerOmitted) {
    return total;
  }
  return undefined;
}

export function DonutPanel({ spec }: { spec: DonutSpec }) {
  const tileId = useId();
  // Stable, positionally-indexed ref list — category, value, then the
  // optional center scalar. Order is fixed so the resolved-column indices
  // don't shift as the optional center ref comes and goes.
  const refs = [spec.category, spec.value, ...(spec.center === undefined ? [] : [spec.center])];
  const cols = useResolvedColumns(refs);
  const categoryCol = cols[0] ?? [];
  const valueCol = cols[1] ?? [];
  const centerCol = spec.center === undefined ? undefined : (cols[2] ?? []);

  // One slice per row: category names it, value sizes it — built by the
  // SSOT slice predicate shared with `validateDashboardSpec`, so a donut
  // that validated draws exactly these slices.
  const initialSlices = capSlices(donutSlices(categoryCol, valueCol), spec.maxSlices);

  // Shared categorical color assignment: same category → same token across
  // renders, in resolved order, cycling the cartesian palette. The `:colors`
  // override map pins specific categories. Resolved against the displayed
  // category list (including any synthesized `Other`).
  const scale = categoryColorScale(
    initialSlices.map((s) => s.name),
    spec.colors,
  );
  const slices = initialSlices.map((s) => ({ ...s, fill: mantineColorVar(scale.get(s.name) ?? 'blue') }));
  // const fillFor = (name?: string) => mantineColorVar(name ? (scale.get(name) ?? 'blue') : 'dark');

  const fallbackHints = numberHints(slices.map((s) => s.value));
  const formatValue = SLICE_FORMATTERS[spec.format] ?? ((n: number) => formatNumber(n, fallbackHints));

  const total = slices.reduce((sum, s) => sum + s.value, 0);

  // Slice value labels. `percent` (default) shows each slice's share of the
  // total; `value` shows the formatted magnitude; `none` hides labels.
  const LabelRenderers = {
    percent: (s: DonutSlice) => (total === 0 ? '0.0%' : formatPercent((s.value / total) * 100)),
    value: (s: DonutSlice) => formatValue(s.value),
    none: () => '',
  } as const;
  const labelRenderer = LabelRenderers[spec.labels];
  const showLabels = spec.labels !== 'none';
  // recharts spreads the datum onto the label render props AND exposes it under
  // `payload`; read the slice back from `payload` so the label maths run over
  // the same `DonutSlice` shape we built (name + numeric value).
  const renderSliceLabel = (props: PieLabelRenderProps) => labelRenderer(props.payload as DonutSlice);

  // Center KPI readout. The scalar `:center` wins when present; otherwise,
  // when only `:center-label` is set, the slice values are auto-summed. The
  // readout renders only when a caption is configured.
  const centerScalar = centerCol === undefined ? undefined : cellScalar(centerCol.at(-1));
  const centerValue = pickCenterValue(centerScalar, spec.center === undefined, total);

  const getCsv = () =>
    csvFromRows(slices, [
      { source: 'name', label: 'Category' },
      { source: 'value', label: 'Value' },
    ]);
  const tileName = `donut-${slugify(spec.centerLabel ?? slices.at(0)?.name ?? 'donut')}`;

  return (
    <TileFrame getCsv={getCsv} id={tileId} inspect={() => ({ spec, slices })} name={tileName}>
      <div className={classes.donutChart}>
        <ResponsiveContainer>
          <PieChart className={classes.pie} margin={{ top: 0, right: 30, bottom: 0, left: 30 }}>
            <Pie
              data={slices}
              dataKey="value"
              fontSize={11}
              innerRadius="60%"
              isAnimationActive={false}
              label={showLabels ? renderSliceLabel : undefined}
              nameKey="name"
              outerRadius="80%"
              paddingAngle={1}
              shape={(props) => <Sector {...props} strokeWidth={0} />}
            />
            <Tooltip
              // allowEscapeViewBox={{ x: true, y: true }}
              formatter={(value) => formatValue(typeof value === 'number' ? value : Number(value))}
              isAnimationActive={false}
              wrapperClassName={classes.tooltip}
            />
            <Label className={classes.centerValue} dy={spec.centerLabel === undefined ? 0 : -8} position="center">
              {centerValue === undefined ? EMPTY : formatValue(centerValue)}
            </Label>
            {spec.centerLabel !== undefined && (
              <Label className={classes.centerLabel} dy={12} position="center">
                {spec.centerLabel}
              </Label>
            )}
            {spec.legend && (
              <Legend
                align="center"
                formatter={(value) => <span style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{value}</span>}
                iconSize={8}
                verticalAlign="bottom"
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </TileFrame>
  );
}
